import type { GraphNode, ParsedRuleGraph } from "@packages/commons";
import produce from "immer";
import { cloneDeep, set } from "lodash";
import uniq from "lodash/uniq";
import { useMemo } from "react";
import pako from 'pako';
import { eigenvectorCentrality } from "./math";
import { graphlib, Node as DagreNode } from "@dagrejs/dagre";
import { ensureParsedGraph, iterateGraphNodeValues, RawGraphNode, RawRuleGraph, StoredRuleGraph } from "@imminently/decisively-core";

/**
 * TODO want a better name for this.
 * George suggested AttributeType, but attribute.type is the ValueType, which is confusing.
 * Any suggestion would be appreciated.
 * Also suggested AttributeCategory, but that conflicts with attribute.category
 */
export enum GraphNodeType {
  GOAL = "goal",
  INPUT = "input",
  DERIVED = "derived",
  IDENTIFIER = "identifier",
  MISSING = "waiting_for_save",
  VALIDATION = "validation",
};

export type GraphLibNode = DagreNode<GraphNode>;
export interface GraphNodeWithType extends GraphNode { nodeType: GraphNodeType };

export type NodeGroups = {
  /**
   * - It has no parents (predecessors)
   * - It is not an identifier (identifier: true in node)
   * - It is not a foreign key (fk: true in node)
   * - It can be on any entity
   */
  goals: GraphLibNode[];
  /**
   * - has no successors
   * - has no "condtions", nor "rows"
   */
  inputs: GraphLibNode[];
  /** node is an "identifier node" if it has "identifier: true" */
  identifiers: GraphLibNode[];
  /** neither a "goal", nor an "input" */
  derived: GraphLibNode[];
}

const defaultNodeGroups: NodeGroups = { goals: [], inputs: [], derived: [], identifiers: [] };

export const getNodeType = (node: GraphNode | null | undefined) => {
  // if(!node) throw "Cannot get node type! Node not found in graph.";
  // does not exist yet, so return null
  if (!node) return GraphNodeType.MISSING;

  if (node.validation) {
    return GraphNodeType.VALIDATION;
  }

  if (node.goal && !node.fk && !node.identifier) {
    return GraphNodeType.GOAL;
  }

  if (node.identifier) {
    return GraphNodeType.IDENTIFIER;
  }

  if (!node.definedIn) {
    return GraphNodeType.INPUT;
  }

  return GraphNodeType.DERIVED;
};

export type GraphCounts = {
  total: number;
  input: number;
  derived: number;
  goals: number;
  identifiers: number;
};

export const groupGraphNodes = (g: ParsedRuleGraph, entity?: string): NodeGroups => (
  produce(defaultNodeGroups, draft => {
    g.nodes().forEach(n => {
      const node = g.node(n);
      if (entity !== undefined && node.entity !== entity) return void 1;
      if (node.fk) return void 1;
      const predcessors = g.predecessors(n) || [];

      if (predcessors.length === 0 && !node.fk && !node.identifier) {
        return void draft.goals.push(node);
      }

      if (node.identifier) {
        return void draft.identifiers.push(node);
      }

      const successors = g.successors(n) || [];
      if (successors.length === 0 && !node.conditions && !node.rows) {
        return void draft.inputs.push(node);
      }

      return void draft.derived.push(node);
    });
  })
);

export const nodeGroupCount = (groups: NodeGroups): GraphCounts => ({
  total: groups.goals.length + groups.inputs.length + groups.derived.length + groups.identifiers.length,
  goals: groups.goals.length,
  input: groups.inputs.length,
  derived: groups.derived.length,
  identifiers: groups.identifiers.length,
});

// ===================================================================================

/**
 * Flat list of graph nodes usable as interview-designer "model inputs": every node that has a
 * description, excluding internal fk attributes, each tagged with a `graph_type` (via
 * `getNodeType`) for consumers that key off it (e.g. GraphAttribute's derived/goal dot).
 *
 * Optionally scoped to the nodes reachable from `goalId` (root/descendants), same as the old
 * `getModelInputsAll`'s goal filtering; pass no goal to get every node in the graph.
 *
 * Replaces `getModelInputsAll` (apps/portal/src/pages/models/shared/getModelInputs.js), which
 * built this same list via `lodash.set(model, \`${entity}.fields.${nodeId}\`, cfg)`. That breaks
 * for any canonical (description-derived) node id containing a "." - lodash re-parses the
 * template string as a nested path instead of treating the id as one literal key, so the node
 * silently lands somewhere else in the object and never makes it into the flattened list.
 */
export const getModelInputs = (graph: StoredRuleGraph | null | undefined, goalId?: string | null): GraphNode[] => {
  if (!graph) return [];

  const g = ensureParsedGraph(graph);

  let scopedIds: string[] | null = null;
  if (goalId) {
    try {
      scopedIds = graphlib.alg.preorder(g, goalId) || [];
    } catch (err) {
      console.error("Error fetching children of goal", (err as Error)?.message);
      scopedIds = null;
    }
  }

  const nodes = scopedIds
    ? (scopedIds.map((id) => g.node(id)).filter(Boolean) as GraphNode[])
    : [...iterateGraphNodeValues(g)];

  return nodes.reduce<GraphNode[]>((acc, node) => {
    if (!node.description || node.fk) return acc;
    acc.push({ ...cloneDeep(node), graph_type: getNodeType(node) });
    return acc;
  }, []);
};

export const gatherInputNodesByGoalId = (graph: StoredRuleGraph | null, goalId: string): string[] => {

  if (graph === null) {
    return [];
  }
  const g = ensureParsedGraph(graph);

  const { inputs } = groupGraphNodes(g);
  const inputsHash = inputs.reduce<Record<string, true>>(
    (a, i) => ({ ...a, [i.id]: true }),
    {},
  );

  const inputNodes = (function gatherSuccessors(nId: string): string[] {
    const acc = inputsHash[nId] ? [nId] : [];

    const outEdges = g.outEdges(nId);
    if (!Array.isArray(outEdges)) return acc;

    const children = outEdges.map(it => it.w);
    return acc.concat(children.reduce<string[]>(
      (a, it) => a.concat(gatherSuccessors(it)),
      [],
    ));
  }(goalId));

  const uniqInputNodes = uniq(inputNodes);

  return uniqInputNodes;
};

// Graph category code ported from decisively audit (utils/GraphFunc.ts)

const getGoal = (goal: string | GraphNode, graph: graphlib.Graph | ParsedRuleGraph) => {
  let goalNode;
  if (typeof goal === "string") {
    // Find the node
    goalNode = graph.node(goal); // TODO: handle description and public name
  } else goalNode = goal;
  return goalNode;
};

/**
 *
 *
 const exampleData = {
    "123": {
      "456": {}
    },
    "789": {},
    "111": {}
  }
 *
 * given data structure like example data, write a function that converts it to an array of objects with children
 */
const convertData = (data: Record<string, any>, transform: (key: string) => any): any[] => {
  const result: any[] = [];
  for (const key in data) {
    const obj = { id: key, ...transform(key), children: convertData(data[key], transform) };
    result.push(obj);
  }
  return result;
};

/**
 * Builds an entity path map from category nodes.
 * Groups nodes by their entity path (the path without the attribute name).
 *
 * Example output:
 * {
 *   "entity/1": [node1],
 *   "entity/1/sub/1/other/2": [node2, node3]
 * }
 */
export const buildEntityPathMap = (categoryNodes: GraphNode[]): Map<string, GraphNode[]> => {
  const entityPathMap = new Map<string, GraphNode[]>();

  categoryNodes.forEach((node) => {
    const path = node.path || node.id;
    const pathSegments = path.split("/");
    const entityPath = pathSegments.slice(0, -1).join("/");

    if (!entityPathMap.has(entityPath)) {
      entityPathMap.set(entityPath, []);
    }
    entityPathMap.get(entityPath)!.push(node);
  });

  return entityPathMap;
};

/**
 * Finds the identifier node for a given entity path from the entity path map.
 * Returns the identifier node if found, null otherwise.
 */
export const findIdentifierForEntityPath = (
  entityPath: string,
  entityPathMap: Map<string, GraphNode[]>
): GraphNode | null => {
  const nodes = entityPathMap.get(entityPath);
  if (!nodes) return null;
  return nodes.find((n) => n.identifier) || null;
};

export const getCategories = (goal: string, graph: ParsedRuleGraph) => {
  // First, collect only category nodes (not identifiers by default)
  const categoryNodes: GraphNode[] = [];
  const seen = new Set<string>();

  const collectCategories = (node: GraphNode | null) => {
    if (!node || node.hidden) {
      return;
    }
    const nodeKey = node.path || node.id;
    if (seen.has(nodeKey)) {
      return;
    }
    seen.add(nodeKey);

    // Only include category nodes (not identifiers)
    if (node.category) {
      categoryNodes.push(node);
    }

    const children = graph.successors(nodeKey);
    if (children && children.length > 0) {
      children.forEach((childId) => {
        const childNode = graph.node(childId);
        if (childNode) {
          collectCategories(childNode);
        }
      });
    }
  };


  for (const node of Object.values(graph._nodes as any) as any) {
    if (node.identifier && node.category && !node.hidden) {
      categoryNodes.push(node);
    }
  }

  const goalNode = getGoal(goal, graph);
  collectCategories(goalNode);

  // Build entity path map from category nodes only
  const entityPathMap = buildEntityPathMap(categoryNodes);

  // Find entity paths that have multiple category nodes - these need an identifier as parent
  const entityPathsNeedingIdentifier: string[] = [];
  entityPathMap.forEach((nodes, entityPath) => {
    if (nodes.length > 1) {
      entityPathsNeedingIdentifier.push(entityPath);
    }
  });


  // For entity paths with multiple category nodes, find the grouping node
  // Priority: 1) categoryTitle node, 2) identifier node
  const groupingNodesByEntityPath = new Map<string, GraphNode>();
  entityPathsNeedingIdentifier.forEach((entityPath) => {
    const nodesAtPath = entityPathMap.get(entityPath) || [];
    const graphNodes = graph._nodes ? Object.values(graph._nodes) : [];

    // First: check if any category node at this path has categoryTitle
    const categoryTitleNode = nodesAtPath.find((n) => n.categoryTitle);
    if (categoryTitleNode) {
      groupingNodesByEntityPath.set(entityPath, categoryTitleNode);
      return;
    }

    // Second: look for a pure categoryTitle node in the graph (not a category itself)
    const pureCategoryTitleNode = graphNodes.find((n) => {
      if (!n.categoryTitle) return false;
      const nodePath = n.path || n.id;
      const nodeEntityPath = nodePath.split("/").slice(0, -1).join("/");
      return nodeEntityPath === entityPath;
    });
    if (pureCategoryTitleNode) {
      groupingNodesByEntityPath.set(entityPath, pureCategoryTitleNode);
      return;
    }

    // Third: check if any category node at this path is also an identifier
    const categoryIdentifier = nodesAtPath.find((n) => n.identifier);
    if (categoryIdentifier) {
      groupingNodesByEntityPath.set(entityPath, categoryIdentifier);
      return;
    }

    // Fourth: look for a pure identifier node in the graph
    const pureIdentifierNode = graphNodes.find((n) => {
      if (!n.identifier) return false;
      const nodePath = n.path || n.id;
      const nodeEntityPath = nodePath.split("/").slice(0, -1).join("/");
      return nodeEntityPath === entityPath;
    });

    if (pureIdentifierNode) {
      groupingNodesByEntityPath.set(entityPath, pureIdentifierNode);
    }
  });

  // Collect grouping nodes that need to be added (only pure grouping nodes, not category nodes)
  const groupingNodesToAdd: GraphNode[] = [];
  groupingNodesByEntityPath.forEach((groupingNode) => {
    // Only add if it's not already in categoryNodes
    if (!groupingNode.category) {
      groupingNodesToAdd.push(groupingNode);
    }
  });

  // Add grouping nodes to the entity path map
  groupingNodesToAdd.forEach((node) => {
    const nodePath = node.path || node.id;
    const entityPath = nodePath.split("/").slice(0, -1).join("/");
    if (!entityPathMap.has(entityPath)) {
      entityPathMap.set(entityPath, []);
    }
    entityPathMap.get(entityPath)!.push(node);
  });

  // Sort entity paths by length (shorter paths = parents, should be processed first)
  const sortedEntityPaths = Array.from(entityPathMap.keys()).sort((a, b) => {
    const lenA = a.split("/").length;
    const lenB = b.split("/").length;
    if (lenA !== lenB) return lenA - lenB;
    return a.localeCompare(b);
  });

  // Build the tree structure
  const entityPathToTreeNode = new Map<string, GraphNodeWithChildren>();
  const rootNodes: GraphNodeWithChildren[] = [];

  sortedEntityPaths.forEach((entityPath) => {
    const nodesAtPath = entityPathMap.get(entityPath) || [];

    // Find parent entity path (longest prefix match)
    let parentEntityPath: string | null = null;
    for (let i = sortedEntityPaths.indexOf(entityPath) - 1; i >= 0; i--) {
      const candidatePath = sortedEntityPaths[i];
      if (entityPath.startsWith(candidatePath + "/")) {
        parentEntityPath = candidatePath;
        break;
      }
    }

    // Get parent tree node if exists
    const parentTreeNode = parentEntityPath ? entityPathToTreeNode.get(parentEntityPath) : null;

    // Check if there's a grouping node for this path (meaning >1 nodes need wrapping)
    const groupingNode = groupingNodesByEntityPath.get(entityPath);

    if (nodesAtPath.length > 1 && groupingNode) {
      // More than 1 node: wrap them in the grouping node
      const groupingTreeNode: GraphNodeWithChildren = {
        ...groupingNode,
        children: [],
      };

      // Set as representative for this entity path
      entityPathToTreeNode.set(entityPath, groupingTreeNode);

      // Add grouping node to parent or root
      if (parentTreeNode) {
        parentTreeNode.children.push(groupingTreeNode);
      } else {
        rootNodes.push(groupingTreeNode);
      }

      // Add other nodes as children of the grouping node
      nodesAtPath.forEach((node) => {
        if (node !== groupingNode) {
          const childTreeNode: GraphNodeWithChildren = {
            ...node,
            children: [],
          };
          groupingTreeNode.children.push(childTreeNode);
        }
      });
    } else {
      // Single node (or no grouping node found): add directly
      nodesAtPath.forEach((node, index) => {
        const treeNode: GraphNodeWithChildren = {
          ...node,
          children: [],
        };

        // First node becomes representative for this entity path
        if (index === 0) {
          entityPathToTreeNode.set(entityPath, treeNode);
        }

        // Add to parent or root
        if (parentTreeNode) {
          parentTreeNode.children.push(treeNode);
        } else {
          rootNodes.push(treeNode);
        }
      });
    }
  });

  return rootNodes;
};

export type GraphNodeWithChildren = GraphNode & { children: GraphNodeWithChildren[] };

export const uncompressGraph = (compressed: any) => {
  try {
    const c = pako.inflate(compressed, { to: 'string' });
    return JSON.parse(c);
  } catch (error) {
    console.log('Failed to uncompress graph', error);
  }
};


export const useGraphStatistics = (graph: any, debug: boolean = false) => {
  let counts: GraphCounts = {
    total: 0,
    input: 0,
    derived: 0,
    goals: 0,
    identifiers: 0,
  };
  let interestingNodes: any[] = [];

  if (!graph) return { counts, interestingNodes };

  let sinks = graph.sinks();
  let sources = graph.sources();

  graph.nodes().forEach(n => {
    let node = graph.node(n);
    if (node.fk) return;
    if (debug && node.hidden) return;
    if (sinks.indexOf(n) > -1) {
      // Some inputs are actually derived - if they have a value
      if (node.value || node.conditions || node.rows) counts.derived = counts.derived + 1;
      else counts.input = counts.input + 1;
    } else if (sources.indexOf(n) > -1) {
      counts.goals = counts.goals + 1;
    } else {
      counts.derived = counts.derived + 1;
    }
    counts.total = counts.total + 1; // always increase total
  });

  let eigens = eigenvectorCentrality(graph);

  // Sort them
  let data = Array.from(eigens).sort((a, b) => {
    if (a[1] > b[1]) return -1;
    else if (a[1] === b[1]) return 0;
    return 1;
  });

  // Loop through the vectors and return the top 10 inputs
  let count = 0;
  let i = 0;
  while (count < 10 && i < data.length) {
    let info = data[i];
    if (!info) {
      count = 11;
      break;
    }
    if (sinks.indexOf(info[0]) > -1) {
      // it's an input node
      let node = graph.node(info[0]);
      if (node) {
        interestingNodes.push(node);
        count++;
      }
    }
    i++;
  };

  return { counts, interestingNodes };
};

export const jsonPayloadToDescriptions = (payload, graph) => {
  // change this to use the descriptions in the graph instead of GUIDs
  try {
    let output = typeof payload === "string" ? JSON.parse(payload) : payload;
    const convertValue = (value) => {
      if (Array.isArray(value)) {
        return value.map(convertValue);
      }
      if (value && Object.prototype.toString.call(value) === "[object Object]") {
        const newValue = {};
        recurse(Object.entries(value), [], newValue);
        return newValue;
      }
      return value;
    };
    const recurse = (attributes, path, newData) => {
      let dupes: any = [];
      for (const a of attributes) {
        if (a[0] === "@id") {
          set(newData, [...path, "@id"], a[1]);
          continue;
        }

        let label = a[0];
        let node = graph?.node?.(a[0]);
        if (node) {
          label = node.description;
          if (dupes.indexOf(node.description) !== -1) {
            // An attribute of this name was already used, this means it's in a name space
            // Use the file path to this attribute instead
            label = `${node.definedIn}/${node.description}`;
          } else dupes.push(node.description);
        }

        if (Array.isArray(a[1])) {
          set(newData, [...path, label], a[1].map(convertValue));
        } else {
          set(newData, [...path, label], convertValue(a[1]));
        }
      }
    };
    let newData = {};
    recurse(Object.entries(output.data), [], newData);
    return JSON.stringify({ data: newData }, null, 2);
  } catch (error) {
    console.log("Error converting value to graph descriptions", error);
    // Do nothing - revert to using the string provided
  }
  return payload;
};
